"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-16 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <>
      {}
      <div className="hidden md:block">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-8">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="group relative py-2 text-sm font-medium tracking-wide text-foreground transition-colors hover:text-foreground">
                    {item.label}
                    <span className="absolute bottom-0 left-0 h-[2px] w-full origin-right scale-x-0 bg-secondary transition-transform duration-300 ease-out group-hover:origin-left group-hover:scale-x-100" />
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {}
      <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {}
      {isOpen && <div className="absolute top-full left-0 w-full border-b border-border bg-background/95 p-6 shadow-lg backdrop-blur-md md:hidden">
          <nav className="flex flex-col space-y-6">
            {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                <Link to={item.href || "#"} className="text-lg font-medium tracking-wide text-foreground transition-colors hover:text-secondary" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>
              </div>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out", scrolled ? "border-b border-border bg-background/80 backdrop-blur-md" : "border-b border-transparent bg-background")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6 lg:px-8">
        <Link to="/" className="flex items-center shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/cosmic_exim_llc_logo.png" alt="Cosmic EXIM LLC" className="h-7 w-auto" />
        </Link>
        
        <MenuProvider menu_id="45">
          <HeaderMenuConsumer />
        </MenuProvider>
      </div>
    </header>;
}